import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API } from "../../lib/api";
import { MagnifyingGlass, Download, Trash } from "@phosphor-icons/react";

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [partyNotes, setPartyNotes] = useState("");
  const [partyNotesDirty, setPartyNotesDirty] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [gameLink, setGameLink] = useState("");
  const [gameLinkBusy, setGameLinkBusy] = useState(false);
  const [emailLog, setEmailLog] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const reload = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    api.get(`/admin/bookings?${params.toString()}`).then(r => setBookings(r.data));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [status]);

  // Auto-open booking when ?focus=<id> is present (deep link from Today panel)
  useEffect(() => {
    const focusId = searchParams.get("focus");
    if (!focusId) return;
    if (selected?.id === focusId) return;
    let cancelled = false;
    (async () => {
      const local = bookings.find(b => b.id === focusId);
      const target = local || (await api.get(`/admin/bookings/${focusId}`).then(r => r.data).catch(() => null));
      if (cancelled || !target) return;
      setSelected(target);
      setNotes(target.admin_notes || "");
      setGameLink(target.game_link || "");
      setPartyNotes(target.party_notes || "");
      setPartyNotesDirty(false);
    })();
    return () => { cancelled = true; };
  }, [searchParams, bookings, selected?.id]);

  const dietary = useMemo(() => bookings.filter(b => b.dietary_notes), [bookings]);
  const cancelledCount = useMemo(() => bookings.filter(b => b.status === "cancelled").length, [bookings]);

  const purgeCancelled = async () => {
    if (!window.confirm(`Permanently delete ${cancelledCount} cancelled booking${cancelledCount === 1 ? "" : "s"} and their email/payment logs? This can't be undone.`)) return;
    try {
      const r = await api.post("/admin/bookings/purge-cancelled");
      alert(`Cleared ${r.data.deleted} cancelled booking${r.data.deleted === 1 ? "" : "s"}.`);
      reload();
    } catch (e) {
      alert(`Couldn't clear: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Permanently delete this booking and its email/payment logs? This can't be undone.")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      setSelected(null);
      reload();
    } catch (e) {
      alert(`Couldn't delete: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // Load email log whenever a booking is opened
  useEffect(() => {
    if (!selected?.id) { setEmailLog([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/bookings/${selected.id}/emails`);
        if (!cancelled) setEmailLog(r.data || []);
      } catch {
        if (!cancelled) setEmailLog([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selected?.id]);

  const exportCsv = async () => {
    const token = localStorage.getItem("ms_admin_token");
    const r = await fetch(`${API}/admin/bookings/export/csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "bookings.csv"; a.click();
  };

  const updateBooking = async (id, payload) => {
    await api.patch(`/admin/bookings/${id}`, payload);
    reload();
    if (selected?.id === id) setSelected({ ...selected, ...payload });
  };

  return (
    <div className="p-5 md:p-12" data-testid="admin-bookings">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="label-eyebrow text-ms-stone">Reservations</p>
          <h1 className="serif text-3xl md:text-4xl text-ms-earth mt-2">Bookings</h1>
        </div>
        <div className="flex gap-2 items-center">
          {cancelledCount > 0 && (
            <button
              onClick={purgeCancelled}
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-ms-danger/40 text-ms-danger rounded-md text-sm hover:bg-ms-danger/10"
              data-testid="purge-cancelled-btn"
              title="Hard-delete every cancelled booking and its email/payment logs."
            >
              <Trash size={14} /> Clear {cancelledCount} cancelled
            </button>
          )}
          <button onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2.5 border border-ms-edge rounded-md text-sm hover:bg-ms-cream" data-testid="export-csv-btn"><Download size={16} /> Export CSV</button>
        </div>
      </div>

      <div className="mt-6 flex gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px] relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-ms-stone" size={16} />
          <input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>{if (e.key==='Enter') reload();}}
            placeholder="Search name, email, phone…" className="w-full pl-10 pr-3 py-2.5 border border-ms-edge rounded-md text-sm" data-testid="bookings-search" />
        </div>
        <select value={status} onChange={e=>setStatus(e.target.value)} className="border border-ms-edge rounded-md px-3 py-2.5 text-sm" data-testid="bookings-status-filter">
          <option value="">All statuses</option>
          <option value="confirmed">Confirmed</option>
          <option value="pending">Pending</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {dietary.length > 0 && (
        <div className="mt-6 bg-ms-warn/10 border border-ms-warn/30 rounded-2xl p-5">
          <p className="label-eyebrow text-ms-warn text-xs">Dietary alerts ({dietary.length})</p>
          <ul className="mt-2 text-sm text-ms-earth space-y-1">
            {dietary.slice(0,5).map(b => <li key={b.id}><b>{b.customer_name}</b>: {b.dietary_notes}</li>)}
          </ul>
        </div>
      )}

      {/* Mobile: stacked cards */}
      <div className="mt-6 md:hidden space-y-3" data-testid="bookings-mobile-list">
        {bookings.map(b => (
          <button
            key={b.id}
            onClick={() => { setSelected(b); setNotes(b.admin_notes || ""); setGameLink(b.game_link || ""); }}
            className="w-full text-left bg-white border border-ms-edge rounded-xl p-4 active:bg-ms-cream/40"
            data-testid={`booking-row-${b.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-ms-earth font-medium truncate">{b.customer_name}</p>
                <p className="text-xs text-ms-stone truncate">{b.customer_email}</p>
              </div>
              <StatusBadge status={b.status} />
            </div>
            <div className="mt-3 pt-3 border-t border-ms-edge grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-ms-stone">Guests</p>
                <p className="text-ms-earth font-medium">{b.guest_count}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-ms-stone">Total</p>
                <p className="text-ms-earth font-medium">${b.total_amount}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-ms-stone">Created</p>
                <p className="text-ms-earth font-medium text-xs">{new Date(b.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </button>
        ))}
        {bookings.length === 0 && <p className="text-center text-ms-stone bg-white border border-ms-edge rounded-xl p-8">No bookings found.</p>}
      </div>

      {/* Desktop: table */}
      <div className="mt-6 hidden md:block bg-white border border-ms-edge rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ms-cream border-b border-ms-edge">
            <tr><th className="text-left p-4 label-eyebrow text-xs">Customer</th><th className="text-left p-4 label-eyebrow text-xs">Guests</th><th className="text-left p-4 label-eyebrow text-xs">Total</th><th className="text-left p-4 label-eyebrow text-xs">Status</th><th className="text-left p-4 label-eyebrow text-xs">Created</th><th className="p-4"></th></tr>
          </thead>
          <tbody>
            {bookings.map(b => (
              <tr key={b.id} className="border-b border-ms-edge last:border-0 hover:bg-ms-cream/40 cursor-pointer" onClick={() => { setSelected(b); setNotes(b.admin_notes || ""); setGameLink(b.game_link || ""); setPartyNotes(b.party_notes || ""); setPartyNotesDirty(false); }}>
                <td className="p-4 text-ms-earth"><div>{b.customer_name}</div><div className="text-xs text-ms-stone">{b.customer_email}</div></td>
                <td className="p-4 text-ms-earth">{b.guest_count}</td>
                <td className="p-4 text-ms-earth">${b.total_amount}</td>
                <td className="p-4"><StatusBadge status={b.status} /></td>
                <td className="p-4 text-ms-stone">{new Date(b.created_at).toLocaleDateString()}</td>
                <td className="p-4 text-right text-xs text-ms-brass">View →</td>
              </tr>
            ))}
            {bookings.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-ms-stone">No bookings found.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 md:p-6" onClick={() => { setSelected(null); if (searchParams.get("focus")) { searchParams.delete("focus"); setSearchParams(searchParams, { replace: true }); } }}>
          <div onClick={e=>e.stopPropagation()} className="bg-white rounded-2xl p-5 md:p-8 w-full max-w-2xl space-y-4 max-h-[90vh] overflow-y-auto" data-testid="booking-detail">
            <h2 className="serif text-2xl text-ms-earth">{selected.customer_name}</h2>
            <Detail k="Email" v={selected.customer_email} />
            <Detail k="Phone" v={selected.customer_phone} />
            <Detail k="Guests" v={selected.guest_count} />
            <Detail k="Total" v={`$${selected.total_amount}`} />
            {selected.payment_mode === "split" && (
              <>
                <Detail k="Paid" v={`$${(selected.paid_amount || 0).toFixed(2)}`} />
                <Detail k="Outstanding" v={`$${(selected.total_amount - (selected.paid_amount || 0)).toFixed(2)}`} />
                <Detail k="Payment mode" v="Split" />
                <Detail k="Payment status" v={<PayStatus status={selected.payment_status || "pending"} />} />
              </>
            )}
            <Detail k="Dietary (lead)" v={selected.dietary_notes || "—"} />
            <Detail k="Marketing opt-in" v={selected.marketing_opt_in ? "Yes" : "No"} />
            <Detail
              k="Terms + Privacy"
              v={
                selected.terms_accepted ? (
                  <span className="text-xs text-ms-stone leading-[1.5]" data-testid="booking-terms-consent">
                    <span className="text-ms-success">✓ Accepted</span>
                    {selected.terms_accepted_at && <> · {new Date(selected.terms_accepted_at).toLocaleString()}</>}
                    {selected.terms_accepted_ip && <><br/>IP: <span className="font-mono">{selected.terms_accepted_ip}</span></>}
                    {selected.terms_accepted_user_agent && <><br/><span className="text-ms-stone/70 text-[10px]">{selected.terms_accepted_user_agent}</span></>}
                  </span>
                ) : (
                  <span className="text-ms-stone text-xs" data-testid="booking-terms-consent">Not recorded (legacy booking)</span>
                )
              }
            />
            <Detail k="Status" v={<StatusBadge status={selected.status} />} />

            {/* Shareable booking-page link */}
            {(selected.status === "confirmed" || selected.status === "pending_balance") && (
              <div className="bg-ms-cream/60 border border-ms-edge rounded-md p-4" data-testid="booking-share-block">
                <p className="label-eyebrow text-ms-stone text-[10px]">Shareable booking page</p>
                <p className="text-ms-stone text-xs mt-1.5 leading-[1.55]">Send this link to the lead booker (or anyone in the party). Read-only, no PII, includes the 12-section briefing.</p>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    readOnly
                    value={`${window.location.origin}/booking/${selected.id}`}
                    className="flex-1 font-mono text-[12px] border border-ms-edge bg-white rounded-md px-3 py-2 text-ms-earth"
                    onFocus={e => e.target.select()}
                    data-testid="booking-share-link"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(`${window.location.origin}/booking/${selected.id}`);
                        setShareCopied(true);
                        setTimeout(() => setShareCopied(false), 2000);
                      } catch { /* clipboard unavailable — user can manually select the input */ }
                    }}
                    className="shrink-0 px-3 py-2 text-xs border border-ms-earth text-ms-earth rounded-md hover:bg-ms-earth hover:text-ms-cream"
                    data-testid="booking-share-copy-btn"
                  >
                    {shareCopied ? "Copied ✓" : "Copy"}
                  </button>
                  <a
                    href={`/booking/${selected.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 px-3 py-2 text-xs border border-ms-edge text-ms-stone rounded-md hover:text-ms-earth hover:border-ms-earth"
                    data-testid="booking-share-open"
                  >
                    Open ↗
                  </a>
                </div>
              </div>
            )}

            {/* Per-party notes — shown on the public booking page */}
            <div className="bg-ms-cream/60 border border-ms-edge rounded-md p-4" data-testid="party-notes-block">
              <p className="label-eyebrow text-ms-stone text-[10px]">Notes for this party (visible on their booking page)</p>
              <p className="text-ms-stone text-[11px] mt-1 leading-[1.55]">e.g. &ldquo;We swapped the second stop to Bandit because of your seafood allergy&rdquo; &mdash; appears in the brass-bordered card on the guest&apos;s booking page.</p>
              <textarea
                rows={3}
                value={partyNotes}
                onChange={e => { setPartyNotes(e.target.value); setPartyNotesDirty(true); }}
                placeholder="Leave empty to hide the note card on the booking page."
                className="mt-3 w-full border border-ms-edge bg-white rounded-md px-3 py-2.5 text-sm focus:border-ms-brass outline-none"
                data-testid="party-notes-input"
              />
              {partyNotesDirty && (
                <button
                  onClick={async () => {
                    await updateBooking(selected.id, { party_notes: partyNotes });
                    setPartyNotesDirty(false);
                  }}
                  className="mt-2 px-4 py-1.5 text-xs bg-ms-earth text-ms-cream rounded-md label-eyebrow"
                  data-testid="party-notes-save"
                >
                  Save party note
                </button>
              )}
            </div>

            {selected.payment_mode === "split" && selected.co_guests?.length > 0 && (
              <div className="border-t border-ms-edge pt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="label-eyebrow text-ms-stone text-xs">Co-guest shares</p>
                  <button onClick={async () => {
                    const r = await api.post(`/admin/bookings/${selected.id}/send-reminders`);
                    alert(`Sent ${r.data.sent} reminder(s).`);
                  }} className="text-xs text-ms-brass hover:text-ms-brass-lo label-eyebrow" data-testid="send-reminders-btn">Send reminders →</button>
                </div>
                <div className="divide-y divide-ms-edge">
                  {selected.co_guests.map(cg => (
                    <div key={cg.id} className="py-3 text-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-ms-earth">{cg.name}</p>
                          <p className="text-xs text-ms-stone">{cg.email}{cg.phone ? ` · ${cg.phone}` : ""}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-ms-earth">${cg.share_amount?.toFixed(2)}</p>
                          <p className={`text-xs ${cg.status === "paid" ? "text-ms-success" : "text-ms-warn"}`}>{cg.status}</p>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs">
                        <span className={`px-2 py-0.5 rounded-full ${cg.waiver_accepted ? "bg-ms-success/15 text-ms-success" : "bg-ms-edge text-ms-stone"}`}>
                          Waiver: {cg.waiver_accepted ? "✓ signed" : "not signed"}
                        </span>
                        {cg.dietary_notes && <span className="px-2 py-0.5 rounded-full bg-ms-warn/15 text-ms-earth">Dietary: {cg.dietary_notes}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <label className="block">
              <span className="label-eyebrow text-ms-stone text-xs">Game link (private to this party)</span>
              <div className="mt-2 flex gap-2">
                <input value={gameLink} onChange={e=>setGameLink(e.target.value)} placeholder="https://…"
                  className="flex-1 border border-ms-edge rounded-md px-3 py-2.5 text-sm" data-testid="game-link-input" />
                <button onClick={async () => {
                  setGameLinkBusy(true);
                  try {
                    await api.post(`/admin/bookings/${selected.id}/game-link`, { game_link: gameLink.trim() });
                    setSelected({ ...selected, game_link: gameLink.trim(), game_link_sent_at: gameLink.trim() !== (selected.game_link || "") ? null : selected.game_link_sent_at });
                    reload();
                  } finally { setGameLinkBusy(false); }
                }} disabled={gameLinkBusy} className="px-4 py-2 text-sm border border-ms-edge rounded-md hover:bg-ms-cream disabled:opacity-50" data-testid="game-link-save">Save</button>
                <button onClick={async () => {
                  setGameLinkBusy(true);
                  try {
                    await api.post(`/admin/bookings/${selected.id}/game-link`, { game_link: gameLink.trim() });
                    await api.post(`/admin/bookings/${selected.id}/send-game-link`);
                    setSelected({ ...selected, game_link: gameLink.trim(), game_link_sent_at: new Date().toISOString() });
                    alert("Game link emailed to the whole party.");
                    reload();
                  } catch (e) {
                    alert(e?.response?.data?.detail || "Send failed");
                  } finally { setGameLinkBusy(false); }
                }} disabled={gameLinkBusy || !gameLink.trim()} className="px-4 py-2 text-sm bg-ms-ink text-ms-cream rounded-md disabled:opacity-50" data-testid="game-link-send-now">Send now</button>
              </div>
              <p className="mt-1.5 text-[11px] text-ms-stone" data-testid="game-link-status">
                {selected.game_link_sent_at
                  ? `✓ Sent ${new Date(selected.game_link_sent_at).toLocaleString()}`
                  : (selected.game_link || gameLink.trim())
                    ? "Queued — auto-emails the whole party a few hours before start (Settings → Email controls the timing), or click Send now."
                    : "Paste the private game link — it will auto-email the lead guest and all co-guests before the tour starts."}
              </p>
            </label>

            <div className="border-t border-ms-edge pt-4">
              <p className="label-eyebrow text-ms-stone text-xs mb-3" data-testid="email-log-heading">
                Email delivery log
                {emailLog.length > 0 && <span className="text-ms-brass ml-1.5">({emailLog.length})</span>}
              </p>
              {emailLog.length === 0 ? (
                <p className="text-xs text-ms-stone italic" data-testid="email-log-empty">
                  No emails sent yet for this booking.
                </p>
              ) : (
                <div className="divide-y divide-ms-edge max-h-60 overflow-y-auto -mx-1 px-1" data-testid="email-log-list">
                  {emailLog.map(row => (
                    <div key={row.id} className="py-2.5 text-xs flex items-start gap-3" data-testid={`email-log-row-${row.template_key}`}>
                      <span className={`mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${
                        row.status === "sent" ? "bg-ms-success/15 text-ms-success" :
                        row.status === "mock" ? "bg-ms-edge text-ms-stone" :
                        "bg-ms-danger/15 text-ms-danger"
                      }`}>{row.status}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-ms-earth font-medium truncate">{row.subject || row.template_key}</p>
                        <p className="text-ms-stone text-[11px] mt-0.5">
                          <span className="font-mono">{row.template_key}</span>
                          {" → "}
                          {row.recipient}
                        </p>
                        <p className="text-ms-stone text-[11px] mt-0.5">
                          {new Date(row.sent_at).toLocaleString()} · via {row.channel}
                        </p>
                        {row.error && <p className="text-ms-danger text-[11px] mt-1">{row.error}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <label className="block">
              <span className="label-eyebrow text-ms-stone text-xs">Admin notes</span>
              <textarea rows={3} value={notes} onChange={e=>setNotes(e.target.value)} className="mt-2 w-full border border-ms-edge rounded-md px-3 py-2.5 text-sm" data-testid="booking-notes" />
            </label>
            <div className="flex justify-end gap-2 pt-2 flex-wrap">
              {selected.status === "cancelled" && (
                <button
                  onClick={() => deleteBooking(selected.id)}
                  className="px-4 py-2 text-sm text-ms-danger border border-ms-danger/30 rounded-md hover:bg-ms-danger hover:text-white inline-flex items-center gap-1.5"
                  data-testid="booking-delete-forever"
                  title="Permanently remove from the database. Cancelled bookings only."
                >
                  <Trash size={14} /> Delete forever
                </button>
              )}
              {selected.status === "confirmed" && (
                <button onClick={async () => {
                  if (!window.confirm("Refund this booking via Stripe? Every paid share will be refunded, the seat held will be released, and the customer will be emailed.")) return;
                  try {
                    const r = await api.post(`/admin/bookings/${selected.id}/refund`);
                    const { refunded_count, refunded_amount, failures } = r.data;
                    let msg = `Refunded ${refunded_count} payment(s) for $${(refunded_amount || 0).toFixed(2)}.`;
                    if (failures?.length) {
                      msg += `\n\n${failures.length} failure(s):\n` + failures.map(f => `- ${f.error}`).join("\n");
                    }
                    alert(msg);
                  } catch (e) {
                    alert(`Refund failed: ${e?.response?.data?.detail || e.message}`);
                  }
                  reload();
                  setSelected(null);
                }} className="px-4 py-2 text-sm text-ms-warn hover:bg-ms-warn/10 rounded-md" data-testid="booking-refund">Refund</button>
              )}
              {selected.status !== "cancelled" && <button onClick={() => updateBooking(selected.id, { status: "cancelled" })} className="px-4 py-2 text-sm text-ms-danger" data-testid="booking-cancel">Cancel booking</button>}
              <button onClick={() => updateBooking(selected.id, { admin_notes: notes })} className="px-5 py-2 bg-ms-earth text-ms-cream rounded-md label-eyebrow text-xs" data-testid="booking-save">Save notes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = { confirmed: "bg-ms-success/15 text-ms-success", pending: "bg-ms-warn/15 text-ms-warn", cancelled: "bg-ms-danger/15 text-ms-danger" };
  return <span className={`px-2 py-1 rounded-md text-xs ${map[status] || "bg-ms-edge text-ms-stone"}`}>{status}</span>;
}
function PayStatus({ status }) {
  const map = { fully_paid: "bg-ms-success/15 text-ms-success", deposit_paid: "bg-ms-warn/15 text-ms-warn", partially_paid: "bg-ms-warn/15 text-ms-warn", pending: "bg-ms-edge text-ms-stone" };
  return <span className={`px-2 py-1 rounded-md text-xs ${map[status] || "bg-ms-edge text-ms-stone"}`}>{status.replace(/_/g, " ")}</span>;
}
function Detail({ k, v }) { return <div className="flex items-center justify-between text-sm"><span className="text-ms-stone">{k}</span><span className="text-ms-earth text-right">{v}</span></div>; }
